import React, { useEffect, useState } from "react";
import ProductPreview from "./components/ProductPreview";
import PilotForm from "./components/PilotForm";
import Privacy from "./components/Privacy";
import { siteConfig } from "./siteConfig";
import "./App.css";

const questions = [
  [
    "What does VecTurf help our team do?",
    "Track how your course changes. We survey it through the season and set a baseline with your team; after that every reading is compared to that baseline and to the survey before it, so a slow decline shows up while it is still small and you can check whether the work you did moved it back.",
  ],
  [
    "Do we need a drone or new equipment?",
    "The right data depends on the question and the detail you need. We start by reviewing the survey data available for your course and agree an acquisition plan where more is needed. A drone is not assumed to be part of every pilot.",
  ],
  [
    "Does it replace our greenkeeper or agronomist?",
    "No. Your team provides the local knowledge and professional judgement. VecTurf helps organise the evidence and highlight areas for investigation; it does not replace an on-course assessment.",
  ],
  [
    "Can it tell us whether a treatment worked?",
    "It compares the treated ground before and after, against an untreated control measured over the same window, which nets out the weather and the season. That is a good deal stronger than a before and after alone, and still short of proof: a control cannot account for everything that differs between two pieces of ground. It tells you whether the work is worth repeating, not that it was the only cause.",
  ],
  [
    "Who can see our course data?",
    "The application uses club accounts and course-scoped access. Your team works with the published surveys assigned to your club. We agree access and data handling as part of setting up the pilot.",
  ],
  [
    "What would a pilot involve?",
    "We agree one useful question, review the available data and define a manageable scope together. That gives you a baseline to explore, a way to record observations and a plan for reviewing follow-up data. Timing and cost are agreed for your course.",
  ],
];
function Wordmark() {
  // The lockup carries the name, so it is decorative here: every link that uses
  // it already has an aria-label naming the destination, and an alt of "VecTurf"
  // would just make a screen reader say the name twice.
  return (
    <img className="wordmark" src="/vecturf-lockup.png" alt="" width="680" height="210" />
  );
}
function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

export default function App() {
  // One static legal page, so a router is not worth a dependency. netlify.toml
  // rewrites every path to index.html, so the pathname is all we need; anything
  // unrecognised falls through to the homepage rather than a dead end.
  const isPrivacy = /^\/privacy\/?$/.test(window.location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        document.getElementById("menu-toggle")?.focus();
      }
    };
    if (menuOpen) document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [menuOpen]);
  const closeMenu = () => setMenuOpen(false);
  // On /privacy the in-page anchors have nothing to scroll to, so they become
  // links back to that section of the homepage.
  const hrefFor = (hash) => (isPrivacy ? `/${hash}` : hash);
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="header-inner">
          <a
            className="brand-link"
            href={isPrivacy ? "/" : "#top"}
            aria-label="VecTurf home"
            onClick={closeMenu}
          >
            <Wordmark />
          </a>
          <button
            id="menu-toggle"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="main-navigation"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? "Close" : "Menu"}
            <span aria-hidden="true">{menuOpen ? "−" : "+"}</span>
          </button>
          <nav
            id="main-navigation"
            aria-label="Main navigation"
            className={menuOpen ? "navigation is-open" : "navigation"}
          >
            <a href={hrefFor("#product")} onClick={closeMenu}>
              The platform
            </a>
            <a href={hrefFor("#the-data")} onClick={closeMenu}>
              The data
            </a>
            <a href={hrefFor("#how-it-works")} onClick={closeMenu}>
              How it works
            </a>
            <a href={hrefFor("#what-you-need")} onClick={closeMenu}>
              What you need
            </a>
            <a href={hrefFor("#questions")} onClick={closeMenu}>
              Questions
            </a>
            <a
              href={siteConfig.appUrl}
              className="login-link"
              onClick={closeMenu}
            >
              Log in <Arrow />
            </a>
            <a href={hrefFor("#pilot")} className="nav-cta" onClick={closeMenu}>
              Request a pilot <Arrow />
            </a>
          </nav>
        </div>
      </header>
      {isPrivacy ? <Privacy /> : (
      <main id="main">
        <section
          className="hero section-wrap"
          id="top"
          aria-labelledby="hero-title"
        >
          <div className="hero-eyebrow">
            <span className="tiny-dot" />
            Golf course intelligence
            <span className="hero-edition">
              Built for the people who know the ground.
            </span>
          </div>
          <div className="hero-heading">
            <h1 id="hero-title">
              Your course changes.
              <br />
              <em>Now you can prove it.</em>
            </h1>
            <div className="hero-copy">
              <p>
                We survey. You watch the trend, act on what moved, and see
                whether the work made a difference.
              </p>
              <p className="secondary-copy">
                A baseline for your course, then every survey measured against
                it and against the one before.
              </p>
              <a className="text-link" href={hrefFor("#product")}>
                Take a closer look <Arrow />
              </a>
            </div>
          </div>
          <ProductPreview />
          <div className="hero-footnote">
            <span>We capture and process. You decide and act.</span>
            <span>Measured against your baseline, not a textbook.</span>
          </div>
        </section>
        <section
          className="statement section-wrap"
          aria-labelledby="statement-title"
        >
          <div className="section-label">
            <span>01 / Managing change</span>
            <span className="line" />
          </div>
          <div className="statement-grid">
            <h2 id="statement-title">
              Turf does not fail overnight.
              <br />
              <em>It drifts.</em>
            </h2>
            <div>
              <p>
                A green that is slightly worse than last month, and slightly
                worse again the month after, is hard to see by walking it. It is
                obvious in a line on a chart.
              </p>
              <p>
                We take the surveys and set a baseline for your course. After
                that, every reading is measured against that baseline and
                against the survey before it, so drift shows up while it is
                still small.
              </p>
            </div>
          </div>
          <div className="benefit-row">
            <div>
              <span className="benefit-number">01</span>
              <h3>Set the baseline</h3>
              <p>
                What good looks like on your course, agreed with your team
                rather than taken from a textbook.
              </p>
            </div>
            <div>
              <span className="benefit-number">02</span>
              <h3>Watch the trend</h3>
              <p>
                Each survey compared to the last on the same ground, so a slow
                decline is a line rather than a feeling.
              </p>
            </div>
            <div>
              <span className="benefit-number">03</span>
              <h3>Check the work landed</h3>
              <p>
                Record what you did, then read the same ground afterwards
                against an untreated control.
              </p>
            </div>
          </div>
        </section>
        <section className="field-section" aria-labelledby="field-title">
          <div className="section-wrap field-grid">
            <div className="field-copy">
              <div className="section-label">
                02 / From the desk to the course
              </div>
              <h2 id="field-title">
                The next step
                <br />
                happens <em>out here.</em>
              </h2>
              <p>
                A trend is only worth tracking if someone acts on it. Take the
                course view with you, walk the area that moved, and log what you
                did where you did it, so the next survey can check it.
              </p>
              <a href={hrefFor("#how-it-works")} className="text-link light-link">
                See the workflow <Arrow />
              </a>
              <div className="field-note">
                <span className="note-line" />
                Your experience leads.
                <br />
                The evidence comes with you.
              </div>
            </div>
            <div
              className="field-visual"
              aria-label="Illustrative on-course workflow"
            >
              <div className="field-contours" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="field-location">
                <span className="location-cross" aria-hidden="true">
                  +
                </span>
                <span>
                  Find a place.
                  <br />
                  <strong>Add the context.</strong>
                </span>
              </div>
              <div className="observation-card">
                <div className="observation-top">
                  <span className="small-mark">v</span>
                  <span>On course</span>
                  <span>↗</span>
                </div>
                <span className="eyebrow">Hole 07 / West approach</span>
                <h3>What did you find?</h3>
                <div className="observation-line">
                  <span className="observation-dot" />
                  <p>
                    Inspect the flagged area
                    <span>Start with what you see on the ground.</span>
                  </p>
                </div>
                <div className="observation-line">
                  <span className="observation-dot hollow" />
                  <p>
                    Keep the observation on record
                    <span>Give the next decision some context.</span>
                  </p>
                </div>
                <div className="observation-bottom">
                  Your team. Your judgement.<span>↗</span>
                </div>
              </div>
              <span className="field-caption">
                Illustrative workflow · example data
              </span>
            </div>
          </div>
        </section>
        <section
          className="data-source section-wrap"
          id="the-data"
          aria-labelledby="data-title"
        >
          <div className="section-label">
            <span>03 / Where the readings come from</span>
            <span className="line" />
          </div>
          <div className="statement-grid">
            <h2 id="data-title">
              Nobody walks the course
              <br />
              <em>to collect it.</em>
            </h2>
            <div>
              <p>
                The readings come from satellite imagery of your course. The
                satellite passes over roughly every five days, so the same
                ground can be measured again and again without anyone booking a
                flight, closing a hole or carrying a probe.
              </p>
              <p>
                That is what makes a trend possible. Consistency matters more
                than resolution here: the same measurements, on the same tiles,
                captured the same way, are what let you compare August to March
                and trust the difference.
              </p>
            </div>
          </div>
          <div className="data-row">
            <div>
              <h3>Spatial, not a spot check</h3>
              <p>
                Your course is measured as a grid of tiles rather than a handful
                of sample points, so a finding can say which ground moved and
                how much of it.
              </p>
            </div>
            <div>
              <h3>Nothing to disrupt play</h3>
              <p>
                No hardware in the ground, no flights to schedule, no closures.
                The capture happens whether or not anyone is on the course.
              </p>
            </div>
            <div>
              <h3>The same measures each time</h3>
              <p>
                Greenness, moisture, chlorophyll density and turf health, on
                every survey, so a change is a change in the turf rather than in
                how it was measured.
              </p>
            </div>
            <div>
              <h3>A drone when it earns it</h3>
              <p>
                Satellite carries the trend. Where a question needs finer
                detail, a flight is planned for that question rather than as a
                standing cost.
              </p>
            </div>
          </div>
          <p className="data-footnote">
            <span>
              Survey frequency is agreed with your course. The five day figure
              is how often the satellite passes over, not a promise about how
              often we publish.
            </span>
          </p>
        </section>
        <section
          className="process-section section-wrap"
          id="how-it-works"
          aria-labelledby="process-title"
        >
          <div className="process-intro">
            <div className="section-label">04 / How it works</div>
            <h2 id="process-title">
              We survey.
              <br />
              <em>You track what changes.</em>
            </h2>
            <p>
              The division of labour is the point. Capturing and processing the
              data is our job. Knowing what it means for your course is yours.
            </p>
          </div>
          <ol className="process-list">
            {[
              [
                "We survey the course",
                "Satellite readings through the season, with a drone flight where the question needs the detail. Nothing for your team to operate.",
              ],
              [
                "We set the baseline with you",
                "What good looks like on your ground, per measurement, agreed rather than assumed. Everything afterwards is read against it.",
              ],
              [
                "You watch what moves",
                "Each survey is compared to the baseline and to the one before, on the same tiles. Areas that drift are surfaced with the numbers behind them.",
              ],
              [
                "You check the work landed",
                "Log what you did and where. The next survey reads that same ground against an untreated control, so you find out whether it worked.",
              ],
            ].map(([title, body], i) => (
              <li key={title}>
                <span>0{i + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <span className="process-symbol" aria-hidden="true">
                  ↗
                </span>
              </li>
            ))}
          </ol>
        </section>
        <section
          className="requirements section-wrap"
          id="what-you-need"
          aria-labelledby="requirements-title"
        >
          <div className="section-label">
            <span>05 / What it asks of you</span>
            <span className="line" />
          </div>
          <div className="statement-grid">
            <h2 id="requirements-title">
              Nothing to install.
              <br />
              <em>No new system to feed.</em>
            </h2>
            <div>
              <p>
                The honest answer to "what will this cost my team in time" is
                the one most software avoids giving. Here it is.
              </p>
            </div>
          </div>
          <div className="requirement-row">
            <div>
              <h3>What we need from you</h3>
              <p>
                Your course boundary and hole layout, and someone who knows the
                ground to agree the question worth asking. That is the setup.
              </p>
            </div>
            <div>
              <h3>What you do not need</h3>
              <p>
                No hardware to buy, no sensors in the ground, no drone of your
                own, and no data for your team to enter to keep it working.
              </p>
            </div>
            <div>
              <h3>What it asks week to week</h3>
              <p>
                Nothing, unless you want it. The view updates on its own. Your
                team adds observations only where a record is worth keeping.
              </p>
            </div>
            <div>
              <h3>What it costs</h3>
              <p>
                Scope and cost are agreed together, per course, before any
                commitment. Ask and we will talk about the number in the first
                conversation rather than the third.
              </p>
            </div>
          </div>
        </section>
        <section
          className="pilot-section section-wrap"
          id="pilot"
          aria-labelledby="pilot-title"
        >
          <div className="pilot-panel">
            <div className="pilot-copy">
              <div className="section-label">
                <span className="tiny-dot" />
                The pilot programme
              </div>
              <h2 id="pilot-title">
                Let's start
                <br />
                with <em>your course.</em>
              </h2>
              <p>
                We are taking our first pilot partnerships now. One course, one
                useful question and a scope we agree together.
              </p>
              <PilotForm />
              <small>
                We'll agree the data, timing and cost before any commitment.
              </small>
            </div>
            <div className="pilot-details">
              <span className="eyebrow">A considered first step</span>
              {[
                [
                  "A conversation about your course",
                  "What would help your next maintenance decision?",
                ],
                [
                  "A clear data and review plan",
                  "Understand the evidence available and what's needed.",
                ],
                [
                  "A shared view to work from",
                  "Explore the findings, record observations and plan the follow-up.",
                ],
              ].map(([title, body], i) => (
                <div key={title}>
                  <span>0{i + 1}</span>
                  <p>
                    {title}
                    <strong>{body}</strong>
                  </p>
                </div>
              ))}
              <div className="pilot-details-note">
                Built with your team, around a real course question.
              </div>
            </div>
          </div>
        </section>
        <section
          className="faq-section section-wrap"
          id="questions"
          aria-labelledby="faq-title"
        >
          <div>
            <div className="section-label">06 / Good questions</div>
            <h2 id="faq-title">
              Before you
              <br />
              <em>take a closer look.</em>
            </h2>
          </div>
          <div className="faq-list">
            {questions.map(([question, answer]) => (
              <details key={question}>
                <summary>
                  {question}
                  <span className="faq-plus" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      )}
      <footer className="site-footer section-wrap">
        <div className="footer-main">
          <a href={isPrivacy ? "/" : "#top"} aria-label="VecTurf home">
            <Wordmark />
          </a>
          <p>
            A clearer picture of your course.
            <br />A considered next move.
          </p>
          <a href={hrefFor("#pilot")} className="text-link">
            Explore a pilot <Arrow />
          </a>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} VecTurf</span>
          <span>Golf course intelligence</span>
          <a href="/privacy">Privacy</a>
          {siteConfig.contactEmail && (
            <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>
          )}
          <a href={siteConfig.appUrl}>
            Application login <Arrow />
          </a>
        </div>
      </footer>
    </>
  );
}
